import React, { useState } from "react";
import LogoutIcon from "@mui/icons-material/Logout";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { getInitials, useCurrentUser } from "./useCurrentUser";

// Avatar + logout popover pinned to the bottom of the sidebar (BloomABA pattern).
const AccountMenu = ({ open }: { open: boolean }) => {
  const { user, logout } = useCurrentUser();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (!user) return null;

  const handleClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    handleClose();
    await logout();
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: open ? "flex-start" : "center",
        alignItems: "center",
        padding: 2,
      }}
    >
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={1}
        sx={{ cursor: "pointer" }}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        data-testid="account-menu-trigger"
      >
        <Avatar
          alt={user.name}
          sx={{
            width: 40,
            height: 40,
            bgcolor: "primary.main",
            color: "white",
            fontWeight: "bold",
            fontSize: "0.875rem",
            "&:hover": {
              opacity: 0.8,
            },
          }}
        >
          {getInitials(user.name)}
        </Avatar>
        {open && (
          <Stack sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {user.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {user.roleLabel}
            </Typography>
          </Stack>
        )}
      </Stack>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {user.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user.email}
              </Typography>
            </Box>
            <Divider />
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              fullWidth
            >
              Logout
            </Button>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
};

export default AccountMenu;
